/**
 * Staging kits — what furnishes a room of each space type.
 *
 * A kit is a list of props. A prop is either on the FLOOR (a workbench, a
 * washer, a toilet), HUNG ON SLATWALL from a Trusscore accessory (a rake on a
 * hook, bottles on a shelf, a bundle in a basket), or ON A WALL (a towel bar on
 * the boards). Sizes are in feet: `w` along the wall, `d` out from it, `h` up.
 * Every prop is drawn in the browser by a procedural builder keyed by its `id`
 * (room3d/propBuilder.ts), so the kit never names a file — a curated GLB can
 * take over any prop later through `model`, and the photoreal render set is
 * matched up by the same id.
 *
 * A hung prop names the accessories it can hang from (`hangsOn`, kinds from
 * data/slatwallAccessories.ts). It hangs on one the user put on the wall; if
 * the user has put none on any wall, staging hangs one itself — and that one
 * is part of the staging (it goes with Clear Staging and is never counted).
 * The prop itself is decoration and counts for nothing.
 */

import { SlatWallAccessoryKind } from '../../data/slatwallAccessories';

export type PropKind = 'floor' | 'slatwall' | 'wall' | 'ceiling' | 'yard';

/**
 * Where a prop can go (Prop Studio's "where can this go" boxes; catalogue.ts):
 * on a shelf or in a basket, on one of the hooks, on the floor, hung from
 * the ceiling, or on the wall itself (wall art, a towel bar: held at its
 * `mountHeight`, on the wall's product, off the openings — a wall prop goes
 * nowhere else, so `wall` is on its own). A prop's `places` are tried best first.
 */
export type Placement = 'shelf' | 'basket' | 'hook-single' | 'hook-double' | 'hook-bike' | 'hook-hose' | 'wall' | 'floor' | 'ceiling' | 'yard';
export const PLACEMENTS: Placement[] = ['shelf', 'basket', 'hook-single', 'hook-double', 'hook-bike', 'hook-hose', 'wall', 'floor', 'ceiling', 'yard'];

export interface PropSize { w: number; d: number; h: number }

export interface PropDef {
  id: string;
  label: string;
  kind: PropKind;
  size: PropSize;
  /**
   * Floor props: `wall` stands with its back to a wall (the usual); `free` is
   * placed out in the room, facing its middle; `any` is either, the seed
   * choosing which to try first; `corner` stands in a corner of the room (a
   * shower), tight to both walls — its back to one, its left side to the
   * other — in a corner only, never elsewhere. Hung props are always on a wall.
   */
  anchor?: 'wall' | 'free' | 'any' | 'corner' | 'bay' | 'lawn' | 'window';
  /**
   * Floor props: stands in front of this other prop of the kit (its id),
   * facing it, when that one is in the room — a woman at the vanity, a sofa
   * across from the TV console. Placed right after that prop, whatever its
   * order. Else as `anchor` says.
   */
  faces?: string;
  /**
   * Floor props facing another: how far in front of it (feet, its front to
   * the other's front). Default a hand's gap (stage.ts FACING_GAP); a sofa
   * sits a viewing distance back. A room too small for it brings it closer,
   * down to the hand's gap. The floor between them is kept clear.
   */
  facesGap?: number;
  /**
   * Floor props against a wall: at the middle of its wall only (a TV
   * console), never elsewhere along it. Each wall is tried, in the seed's
   * order; beside a partner, or facing another prop, as usual.
   */
  centred?: boolean;
  /**
   * `anchor: 'bay'` (a car, a truck): in line with a garage door, centred on
   * it, one per door. `heading` is which way it faces — `in`, its back to the
   * door, nose into the room; `out`, nose to the door; `any` (default), the
   * seed's choice. `inside` is how much of its length (`size.d`) is in the
   * room: 1 (default) all of it; 0.5 half, 0.25 a quarter, the rest standing
   * outside through the door, which the room then holds open; 0 on the
   * driveway, outside the closed door.
   */
  heading?: 'in' | 'out' | 'any';
  inside?: number;
  /** The count is fixed: the staging density neither thins nor multiplies it (one woman, however full the room). */
  fixed?: boolean;
  /** Out of the pool (Prop Studio's switch): staging leaves it out everywhere; its spaces, places, box and models are kept for when it is back. */
  disabled?: boolean;
  /*
   * `kind: 'yard'` (a tree, a shrub): outside the house, on the lawn
   * (calculations/plan/yard.ts), staged once per plan (stage.ts stageYard).
   * `anchor: 'lawn'` stands out on the lawn, clear of the house and the
   * slabs, where the windows look; `'window'` stands under an exterior
   * window, whatever its height, a step off the wall (`count: 'each'` = one a
   * window).
   */
  /** Floor props: keep this much clear in front (feet) — room to use it. */
  clearance?: number;
  /** Keep this much clear either side (feet): off other props, and off a wall's end or corner. Default 0. */
  clearSide?: number;
  /** Floor props: stand this far off the wall (feet), on top of the hand's gap every prop keeps. Default 0. */
  clearBack?: number;
  /**
   * Floor props: stand right beside this other prop of the kit (its id) on the
   * same wall, a hand's gap apart, wherever that is possible — a dryer beside
   * the washer. Falls back to any wall if the pair will not fit.
   */
  besides?: string;
  /** Floor props beside a partner: the gap between them (feet). Default a hand's gap (stage.ts PAIR_GAP); 0 = touching (a fryer against the range). */
  besidesGap?: number;
  /**
   * Floor props out in the room (`anchor` free or any): may stand at any
   * angle, facing the room's middle (a person). Default off: square to the
   * walls, as furniture stands (an island parallel to a wall).
   */
  askew?: boolean;
  /** Left out when this other prop of the kit (its id) has been placed — no bike on the floor once one hangs from a bike hook. */
  unless?: string;
  /** Hung props: the accessories it can hang from, best first. */
  hangsOn?: SlatWallAccessoryKind[];
  /**
   * Hung props: how far the bottom of the hung item is below the point the
   * accessory holds it at (feet). The rest of its height (`size.h - drop`) is
   * above — a rake's head sits on the hook, its handle hangs. 0 = it sits ON
   * the accessory (a shelf) or in it (a basket).
   */
  drop?: number;
  /**
   * Hung props: how far the hung item reaches along the wall to either side
   * of the point that holds it (feet), when that is not half its width each
   * way — a bike hung by its front wheel has its frame all to one side.
   */
  reach?: { left: number; right: number };
  /**
   * Wall props: the height above the floor (feet) of the point the prop is
   * held at — its bottom when it has no `drop`; with a `drop`, the point it
   * hangs from (a towel bar's bar), the prop reaching `drop` below it.
   */
  mountHeight?: number;
  /**
   * Held props (hung, wall): where the hold point is along the wall from
   * the box's centre (feet, + to the right); 0 = centred. Prop Studio sets it.
   */
  holdX?: number;
  /**
   * Held props: how far out from the wall the hold point is (feet), in place
   * of the accessory's reach or the builders' 0.3. Prop Studio sets it.
   */
  holdZ?: number;
  /**
   * How many of it a room gets (default 1). Hung props may say `'each'`: one
   * for EVERY accessory of the user's it can hang from — a bike on every bike
   * hook that has the room for it.
   */
  count?: number | 'each';
  /**
   * Placed first = most important. Lower is earlier (default 50); below
   * MAIN_ORDER (stage.ts, 30) it is a main piece: placed once at any density.
   * Prop Studio sets it ("Placed": first 5, main piece 20, the rest 50, last 80).
   */
  order?: number;
  /** A GLB to draw instead of the procedural prop (public/models/<set>/…), when one is curated. */
  model?: string;
  /**
   * Where it can go, best first (catalogue.ts): when set, this is what
   * staging tries, in place of `kind` and `hangsOn`. Prop Studio sets it.
   */
  places?: Placement[];
}

export interface StagingKit {
  spaceType: string;
  props: PropDef[];
  /** The floor props the space's 360 faces (room3d/panorama.ts): the wall most of them stand against. None: a garage door's wall, else the front. */
  feature?: string[];
}

const HOOKS: SlatWallAccessoryKind[] = ['hook-single', 'hook-double'];

const GARAGE: StagingKit = {
  spaceType: 'garage',
  props: [
    { id: 'workbench', label: 'Workbench', kind: 'floor', anchor: 'wall', size: { w: 6, d: 2, h: 3 }, clearance: 2.5, order: 10 },
    { id: 'shelf-unit', label: 'Shelving unit', kind: 'floor', anchor: 'wall', size: { w: 3, d: 1.5, h: 6 }, clearance: 1.5, order: 20 },
    // On the floor only when it could not hang (the hung bike is placed first).
    { id: 'bike', label: 'Bicycle', kind: 'floor', anchor: 'wall', size: { w: 5.6, d: 1.8, h: 3.3 }, clearance: 1, order: 30, unless: 'bike-hung' },
    { id: 'step-ladder', label: 'Step ladder (folded)', kind: 'floor', anchor: 'wall', size: { w: 1.8, d: 0.5, h: 6 }, clearance: 0.5, order: 40 },
    { id: 'bin-stack', label: 'Storage bins', kind: 'floor', anchor: 'wall', size: { w: 1.6, d: 1.4, h: 3.2 }, clearance: 0.5, count: 2, order: 50 },
    { id: 'rake', label: 'Rake', kind: 'slatwall', size: { w: 1.6, d: 0.3, h: 5.2 }, hangsOn: HOOKS, drop: 4.3, order: 60 },
    { id: 'shovel', label: 'Shovel', kind: 'slatwall', size: { w: 0.8, d: 0.3, h: 4.6 }, hangsOn: HOOKS, drop: 4.1, order: 61 },
    { id: 'broom', label: 'Broom', kind: 'slatwall', size: { w: 1, d: 0.3, h: 4.4 }, hangsOn: HOOKS, drop: 4.0, order: 62 },
    { id: 'hose', label: 'Garden hose', kind: 'slatwall', size: { w: 1.4, d: 0.5, h: 1.4 }, hangsOn: ['hook-hose', 'hook-bike'], drop: 1.4, order: 63 },
    { id: 'wall-bin', label: 'Storage bin', kind: 'slatwall', size: { w: 0.5, d: 0.5, h: 0.4 }, hangsOn: ['shelf-24', 'basket-18'], drop: 0, count: 3, order: 70 },
    { id: 'shelf-tools', label: 'Tools on a shelf', kind: 'slatwall', size: { w: 2, d: 0.8, h: 0.7 }, hangsOn: ['shelf-24'], drop: 0, order: 65 },
    // A bike hung by its front wheel from the bike hook — only where there is
    // the drop beneath the hook for it (the rules see to that). Placed FIRST,
    // so nothing on the floor takes the space under the hook before it, and
    // the floor bike then stays away.
    // Hung by its front wheel, the wheels are under the hook and the frame,
    // seat and bars reach along the wall to one side (propBuilder.bikeHung).
    { id: 'bike-hung', label: 'Bike on a bike hook', kind: 'slatwall', size: { w: 3.3, d: 1.8, h: 6.0 }, hangsOn: ['hook-bike'], drop: 5.7, reach: { left: 1.2, right: 2.3 }, order: 5, count: 'each' },
    { id: 'helmet', label: 'Bike helmet', kind: 'slatwall', size: { w: 0.9, d: 0.9, h: 0.6 }, hangsOn: ['hook-single', 'hook-double'], drop: 0.55, order: 66 },
    { id: 'hockey-sticks', label: 'Hockey sticks', kind: 'slatwall', size: { w: 0.6, d: 0.3, h: 5.0 }, hangsOn: ['hook-double', 'hook-single'], drop: 4.6, order: 67 },
    { id: 'skateboard', label: 'Skateboard', kind: 'slatwall', size: { w: 0.7, d: 0.5, h: 2.7 }, hangsOn: ['hook-bike', 'hook-double'], drop: 2.5, order: 68 },
    { id: 'axe', label: 'Axe', kind: 'slatwall', size: { w: 0.6, d: 0.3, h: 2.6 }, hangsOn: ['hook-single'], drop: 2.3, order: 69 },
    { id: 'backpack', label: 'Backpack', kind: 'slatwall', size: { w: 1.1, d: 0.7, h: 1.6 }, hangsOn: ['hook-double', 'hook-single'], drop: 1.5, order: 71 },
    { id: 'bike-pump', label: 'Bike pump', kind: 'slatwall', size: { w: 0.3, d: 0.3, h: 2.2 }, hangsOn: ['hook-single'], drop: 2.0, order: 72 },
    { id: 'cord', label: 'Extension cord', kind: 'slatwall', size: { w: 1.2, d: 0.5, h: 1.2 }, hangsOn: ['hook-hose', 'hook-double'], drop: 1.2, order: 73 },
    { id: 'hand-tools', label: 'Garden hand tools', kind: 'slatwall', size: { w: 0.6, d: 0.3, h: 1.1 }, hangsOn: ['hook-single'], drop: 1.0, order: 74 },
    { id: 'boots-basket', label: 'Boots in a basket', kind: 'slatwall', size: { w: 1.0, d: 0.8, h: 1.0 }, hangsOn: ['basket-18'], drop: 0, order: 75 },
    { id: 'balls-basket', label: 'Balls in a basket', kind: 'slatwall', size: { w: 1.2, d: 0.8, h: 0.8 }, hangsOn: ['basket-18'], drop: 0, order: 76 },
    { id: 'shelf-helmet', label: 'Helmet on a shelf', kind: 'slatwall', size: { w: 1.8, d: 0.8, h: 0.7 }, hangsOn: ['shelf-24'], drop: 0, order: 77 },
    { id: 'shelf-drill', label: 'Drill on a shelf', kind: 'slatwall', size: { w: 1.8, d: 0.8, h: 0.8 }, hangsOn: ['shelf-24'], drop: 0, order: 78 },
  ],
};

const LAUNDRY: StagingKit = {
  spaceType: 'laundry-room',
  feature: ['washer', 'dryer'],
  props: [
    { id: 'washer', label: 'Washer', kind: 'floor', anchor: 'wall', size: { w: 2.3, d: 2.3, h: 3.2 }, clearance: 2.5, order: 10 },
    { id: 'dryer', label: 'Dryer', kind: 'floor', anchor: 'wall', size: { w: 2.3, d: 2.3, h: 3.2 }, clearance: 2.5, order: 11, besides: 'washer' },
    { id: 'hamper', label: 'Laundry hamper', kind: 'floor', anchor: 'wall', size: { w: 1.5, d: 1.3, h: 2 }, clearance: 0.5, order: 30 },
    { id: 'shelf-bottles', label: 'Detergent on a shelf', kind: 'slatwall', size: { w: 2, d: 0.8, h: 0.9 }, hangsOn: ['shelf-24'], drop: 0, count: 2, order: 40 },
    { id: 'basket', label: 'Laundry in a basket', kind: 'slatwall', size: { w: 1, d: 0.6, h: 0.5 }, hangsOn: ['basket-18'], drop: 0, count: 2, order: 50 },
    { id: 'ironing-board', label: 'Ironing board', kind: 'slatwall', size: { w: 1.2, d: 0.3, h: 4.6 }, hangsOn: ['hook-double', 'hook-single', 'hook-bike'], drop: 4.1, order: 60 },
  ],
};

const BATHROOM: StagingKit = {
  spaceType: 'washroom',
  feature: ['vanity', 'toilet'],
  props: [
    { id: 'vanity', label: 'Vanity', kind: 'floor', anchor: 'wall', size: { w: 3, d: 1.8, h: 2.9 }, clearance: 2.5, order: 10 },
    { id: 'toilet', label: 'Toilet', kind: 'floor', anchor: 'wall', size: { w: 1.5, d: 2.3, h: 2.6 }, clearance: 2, order: 20 },
    { id: 'plant', label: 'Plant', kind: 'floor', anchor: 'wall', size: { w: 1.1, d: 1.1, h: 3.2 }, clearance: 0.3, order: 40 },
    // Held at the bar, 4 ft up; the towel hangs the prop's whole height below it.
    { id: 'towel-bar', label: 'Towel bar', kind: 'wall', size: { w: 2, d: 0.3, h: 1.6 }, mountHeight: 4, drop: 1.6, order: 30 },
    { id: 'basket', label: 'Towels in a basket', kind: 'slatwall', size: { w: 1, d: 0.6, h: 0.5 }, hangsOn: ['basket-18'], drop: 0, order: 50 },
  ],
};

const LIVING: StagingKit = {
  spaceType: 'living-space',
  feature: ['console'],
  props: [
    { id: 'sofa', label: 'Sofa', kind: 'floor', anchor: 'wall', size: { w: 6.5, d: 3, h: 2.8 }, clearance: 3, order: 10 },
    { id: 'console', label: 'TV console', kind: 'floor', anchor: 'wall', size: { w: 5, d: 1.5, h: 1.8 }, clearance: 3, order: 20 },
    { id: 'bookcase', label: 'Bookcase', kind: 'floor', anchor: 'wall', size: { w: 2.6, d: 1.1, h: 6 }, clearance: 1.5, order: 30 },
    { id: 'plant', label: 'Plant', kind: 'floor', anchor: 'wall', size: { w: 1.1, d: 1.1, h: 3.2 }, clearance: 0.3, order: 40 },
    { id: 'shelf-books', label: 'Books on a shelf', kind: 'slatwall', size: { w: 2, d: 0.8, h: 0.9 }, hangsOn: ['shelf-24'], drop: 0, count: 2, order: 50 },
  ],
};

/** A commercial kitchen: steel shelving, a prep table, bins -- Prop Studio adds the real equipment. */
const KITCHEN: StagingKit = {
  spaceType: 'commercial-kitchen',
  // Its own prop ids (not the garage's workbench and shelving): its models and placements are its own in Prop Studio.
  feature: ['prep-table', 'kitchen-shelving'],
  props: [
    { id: 'prep-table', label: 'Prep table', kind: 'floor', anchor: 'wall', size: { w: 6, d: 2.5, h: 3 }, clearance: 3, order: 10 },
    { id: 'kitchen-shelving', label: 'Steel shelving', kind: 'floor', anchor: 'wall', size: { w: 4, d: 1.5, h: 6 }, clearance: 2, count: 2, order: 20 },
    { id: 'kitchen-bins', label: 'Bins', kind: 'floor', anchor: 'wall', size: { w: 2, d: 1.6, h: 3 }, clearance: 1, order: 30 },
    { id: 'wall-bin', label: 'Storage bin', kind: 'slatwall', size: { w: 0.5, d: 0.5, h: 0.4 }, hangsOn: ['shelf-24', 'basket-18'], drop: 0, count: 3, order: 70 },
  ],
};

export const STAGING_KITS: Record<string, StagingKit> = {
  'garage': GARAGE,
  'workshop': { ...GARAGE, spaceType: 'workshop' },
  'shed': { ...GARAGE, spaceType: 'shed', props: GARAGE.props.filter((p) => p.id !== 'bike') },
  'laundry-room': LAUNDRY,
  'washroom': BATHROOM,
  'commercial-kitchen': KITCHEN,
  'living-space': LIVING,
  'basement': { ...LIVING, spaceType: 'basement', props: [...LIVING.props, ...GARAGE.props.filter((p) => p.id === 'shelf-unit' || p.id === 'bin-stack')] },
  'other': { spaceType: 'other', props: GARAGE.props.filter((p) => p.id === 'shelf-unit' || p.id === 'bin-stack' || p.kind === 'slatwall') },
};

/**
 * The yard's props, in every space's kit: outside the house, seen through
 * its windows and doors (stage.ts stageYard). Drawn by the floor builders.
 */
const YARD: PropDef[] = [
  { id: 'tree', label: 'Tree', kind: 'yard', anchor: 'lawn', size: { w: 14, d: 14, h: 24 }, count: 4, order: 90 },
  { id: 'shrub', label: 'Shrub', kind: 'yard', anchor: 'window', size: { w: 2.6, d: 2, h: 2.2 }, count: 'each', order: 91 },
];
for (const kit of Object.values(STAGING_KITS)) kit.props = [...kit.props, ...YARD];

/** The kit for a space type ('other' when the type has none). */
export function kitFor(spaceType: string | undefined | null): StagingKit {
  return STAGING_KITS[spaceType ?? ''] ?? STAGING_KITS.other;
}

/** Every prop id any kit uses — what the builders must know how to draw. */
export function allPropIds(): string[] {
  return [...new Set(Object.values(STAGING_KITS).flatMap((k) => k.props.map((p) => p.id)))];
}
